import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, Crown } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MockInterviewPaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('Verifying your payment...');
  const [creditsAdded, setCreditsAdded] = useState(0);
  const [planName, setPlanName] = useState('');
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    const verify = async () => {
      const orderId = searchParams.get('order_id');
      if (!orderId) {
        setStatus('error');
        setMessage('Invalid callback. No order ID found.');
        return;
      }

      const token = localStorage.getItem('codearena_token');
      if (!token) {
        setStatus('error');
        setMessage('Session expired. Please login again.');
        setTimeout(() => navigate('/codearena/auth'), 3000);
        return;
      }

      try {
        const res = await axios.post(`${API}/mock-interview/verify-payment`,
          { order_id: orderId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data.success) {
          setStatus('success');
          setCreditsAdded(res.data.credits_added || 0);
          setPlanName(res.data.plan_name || '');
          setMessage(`Payment successful! ${res.data.credits_added} interview credits added.`);
        } else {
          setStatus('failed');
          setMessage('Payment verification failed.');
        }
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        if (detail.includes('No successful payment')) {
          setStatus('failed');
          setMessage('Payment was not completed. Please try again.');
        } else if (detail.includes('already verified')) {
          setStatus('success');
          setMessage('Payment was already verified. Your credits are active.');
        } else {
          setStatus('error');
          setMessage('Unable to verify payment. Please check your dashboard.');
        }
      }
    };

    verify();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'radial-gradient(ellipse at top center, #2A1B44 0%, #0B0714 50%, #0B0714 100%)', fontFamily: 'Outfit, sans-serif' }}>
      <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center" style={{ boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}>
        {status === 'verifying' && (
          <>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: '#00E59B20' }}>
              <Loader size={32} className="animate-spin" style={{ color: '#00E59B' }} />
            </div>
            <h2 className="text-xl font-bold text-zinc-900 mb-2">Verifying Payment</h2>
            <p className="text-sm text-zinc-400">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: '#00E59B20' }}>
              <CheckCircle size={32} style={{ color: '#00E59B' }} />
            </div>
            <h2 className="text-xl font-bold text-zinc-900 mb-2" data-testid="payment-success">Payment Successful!</h2>
            {planName && <p className="text-sm font-semibold" style={{ color: '#00E59B' }}>{planName} Plan</p>}
            {creditsAdded > 0 && (
              <p className="text-sm text-zinc-500 mt-2">
                <span className="font-bold text-zinc-900">{creditsAdded}</span> interview credit{creditsAdded > 1 ? 's' : ''} added to your account
              </p>
            )}
            {(() => {
              const returnTo = searchParams.get('return_to');
              const target = returnTo && returnTo.startsWith('/') ? returnTo : '/codearena/dashboard';
              const label = returnTo && returnTo.startsWith('/') ? 'Continue Where You Left Off' : 'Go to Dashboard';
              return (
                <button onClick={() => navigate(target)} data-testid="go-to-dashboard"
                  className="w-full mt-6 flex items-center justify-center gap-2 py-3 rounded-full font-bold text-sm transition-all hover:-translate-y-0.5"
                  style={{ background: '#00E59B', color: '#09090B', boxShadow: '0 0 16px rgba(0,229,155,0.3)' }}>
                  <Crown size={16} /> {label}
                </button>
              );
            })()}
            {countdown !== null && countdown > 0 && (
              <p className="text-sm text-center mt-3" style={{ color: 'rgba(255,255,255,0.4)' }} data-testid="redirect-countdown">
                Redirecting in {countdown}s...
              </p>
            )}
          </>
        )}

        {(status === 'failed' || status === 'error') && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
              <XCircle size={32} className="text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-zinc-900 mb-2" data-testid="payment-failed">
              {status === 'failed' ? 'Payment Failed' : 'Error'}
            </h2>
            <p className="text-sm text-zinc-400">{message}</p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => navigate('/codearena/dashboard')}
                className="flex-1 py-3 rounded-full font-bold text-sm border-2 border-zinc-200 text-zinc-700 hover:border-zinc-300 transition-all">
                Dashboard
              </button>
              <button onClick={() => navigate('/codearena/mock-interview')}
                className="flex-1 py-3 rounded-full font-bold text-sm transition-all hover:-translate-y-0.5"
                style={{ background: '#00E59B', color: '#09090B' }}>
                Try Again
              </button>
            </div>
            {countdown !== null && countdown > 0 && (
              <p className="text-sm text-center mt-3" style={{ color: 'rgba(255,255,255,0.4)' }} data-testid="redirect-countdown-fail">
                Redirecting to dashboard in {countdown}s...
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MockInterviewPaymentCallback;
